import React, { useState, useEffect } from "react";
import {
  Mail,
  Send,
  CheckCircle2,
  AlertTriangle,
  RefreshCw,
  Save,
  Lock,
  Server,
  FileText,
  Clock,
} from "lucide-react";
import { api, getErrorMessage } from "../../../lib/api";
import { toast } from "sonner";

export default function SmtpTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const [form, setForm] = useState({
    smtp_host: "",
    smtp_port: 587,
    smtp_user: "",
    smtp_password: "",
    smtp_from_email: "",
    smtp_from_name: "Rental Tracker Notifications",
    smtp_ssl_tls: "tls",
    has_password: false,
    is_configured: false,
  });

  const [testEmail, setTestEmail] = useState("");
  const [testResult, setTestResult] = useState(null);

  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);

  const loadSmtp = async () => {
    setLoading(true);
    try {
      const res = await api.get("/super-admin/smtp");
      const d = res.data || {};
      setForm((prev) => ({
        ...prev,
        smtp_host: d.smtp_host || "",
        smtp_port: d.smtp_port != null ? Number(d.smtp_port) : 587,
        smtp_user: d.smtp_user || "",
        smtp_password: "", // do not populate password string from server
        smtp_from_email: d.smtp_from_email || "",
        smtp_from_name: d.smtp_from_name || "Rental Tracker Notifications",
        smtp_ssl_tls: d.smtp_ssl_tls || "tls",
        has_password: Boolean(d.has_password),
        is_configured: Boolean(d.is_configured),
      }));
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to load SMTP settings"));
    } finally {
      setLoading(false);
    }
  };

  const loadLogs = async () => {
    setLoadingLogs(true);
    try {
      const res = await api.get("/super-admin/email-logs");
      setLogs(res.data || []);
    } catch (err) {
      console.error("Failed to load email logs:", err);
    } finally {
      setLoadingLogs(false);
    }
  };

  useEffect(() => {
    loadSmtp();
    loadLogs();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.smtp_host || !form.smtp_host.trim()) {
      toast.error("SMTP Host Server is required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/super-admin/smtp", {
        smtp_host: form.smtp_host.trim(),
        smtp_port: Number(form.smtp_port) || 587,
        smtp_user: (form.smtp_user || "").trim(),
        smtp_password: form.smtp_password || "",
        smtp_from_email: (form.smtp_from_email || "").trim(),
        smtp_from_name: (form.smtp_from_name || "Rental Tracker Notifications").trim(),
        smtp_ssl_tls: form.smtp_ssl_tls || "tls",
      });
      toast.success("Global SMTP configuration saved!");
      await loadSmtp();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to save SMTP settings"));
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async (e) => {
    e.preventDefault();
    if (!testEmail.trim() || !testEmail.includes("@")) {
      toast.error("Please enter a valid recipient email");
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const res = await api.post("/super-admin/smtp/test", { to_email: testEmail.trim() });
      setTestResult({
        ok: true,
        message: res.data?.message || `Test email dispatched to ${testEmail}`,
      });
      toast.success("Test email sent successfully!");
    } catch (err) {
      const msg = getErrorMessage(err, "Failed to dispatch test email");
      setTestResult({ ok: false, message: msg });
      toast.error("Test email failed: see diagnostic details below");
    } finally {
      await loadLogs();
      setTesting(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-white tracking-tight flex items-center gap-2">
            <Mail className="w-5 h-5 text-indigo-400" />
            <span>Global Email Server (SMTP) Setup</span>
          </h2>
          <p className="text-xs text-slate-400 mt-0.5">
            Default mail delivery backbone for all tenant accounts. Tenants without custom SMTP inherit these credentials.
          </p>
        </div>

        <div className="flex items-center gap-2">
          {form.is_configured ? (
            <span className="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 flex items-center gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5" />
              <span>Global Server Ready</span>
            </span>
          ) : (
            <span className="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-amber-500/15 text-amber-400 border border-amber-500/30 flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5" />
              <span>Not Fully Configured</span>
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left 2 Cols: Configuration Form */}
        <div className="lg:col-span-2 bg-slate-900/70 border border-slate-800 rounded-2xl p-6 shadow-xl">
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1.5">
                  SMTP Host Server
                </label>
                <div className="relative">
                  <Server className="w-4 h-4 text-slate-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    required
                    value={form.smtp_host}
                    onChange={(e) => setForm({ ...form, smtp_host: e.target.value })}
                    placeholder="e.g. smtp.postmarkapp.com or smtp.sendgrid.net"
                    className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-slate-950/80 border border-slate-700 text-xs text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1.5">
                  Port
                </label>
                <input
                  type="number"
                  required
                  value={form.smtp_port}
                  onChange={(e) => setForm({ ...form, smtp_port: e.target.value })}
                  placeholder="587"
                  className="w-full px-4 py-2.5 rounded-xl bg-slate-950/80 border border-slate-700 text-xs text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1.5">
                  SMTP Username
                </label>
                <input
                  type="text"
                  value={form.smtp_user}
                  onChange={(e) => setForm({ ...form, smtp_user: e.target.value })}
                  placeholder="e.g. apikey or postmaster@domain.com"
                  className="w-full px-4 py-2.5 rounded-xl bg-slate-950/80 border border-slate-700 text-xs text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1.5">
                  SMTP Password / API Key
                </label>
                <div className="relative">
                  <Lock className="w-4 h-4 text-slate-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
                  <input
                    type="password"
                    value={form.smtp_password}
                    onChange={(e) => setForm({ ...form, smtp_password: e.target.value })}
                    placeholder={form.has_password ? "•••••••• (Saved — enter to change)" : "Enter SMTP password or API token"}
                    className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-slate-950/80 border border-slate-700 text-xs text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1.5">
                  Sender From Email Address
                </label>
                <input
                  type="email"
                  required
                  value={form.smtp_from_email}
                  onChange={(e) => setForm({ ...form, smtp_from_email: e.target.value })}
                  placeholder="notifications@rentaltracker.io"
                  className="w-full px-4 py-2.5 rounded-xl bg-slate-950/80 border border-slate-700 text-xs text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1.5">
                  Sender Display Name
                </label>
                <input
                  type="text"
                  value={form.smtp_from_name}
                  onChange={(e) => setForm({ ...form, smtp_from_name: e.target.value })}
                  placeholder="Rental Tracker Notifications"
                  className="w-full px-4 py-2.5 rounded-xl bg-slate-950/80 border border-slate-700 text-xs text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1.5">
                Encryption Protocol
              </label>
              <div className="flex items-center gap-3">
                {[
                  { value: "tls", label: "STARTTLS (Recommended / Port 587)" },
                  { value: "ssl", label: "SSL / TLS (Port 465)" },
                  { value: "none", label: "Plaintext (Port 25 / Local Relays)" },
                ].map((opt) => (
                  <label
                    key={opt.value}
                    className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer"
                  >
                    <input
                      type="radio"
                      name="smtp_ssl_tls"
                      value={opt.value}
                      checked={form.smtp_ssl_tls === opt.value}
                      onChange={(e) => setForm({ ...form, smtp_ssl_tls: e.target.value })}
                      className="accent-teal-500"
                    />
                    <span>{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="pt-3 flex justify-end">
              <button
                type="submit"
                disabled={saving}
                className="px-5 py-2.5 rounded-xl bg-teal-500 hover:bg-teal-400 text-slate-950 font-bold text-xs tracking-wide shadow-lg shadow-teal-500/20 flex items-center gap-2 transition-all disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                <span>{saving ? "Saving..." : "Save Global SMTP Settings"}</span>
              </button>
            </div>
          </form>
        </div>

        {/* Right 1 Col: Live Test Email */}
        <div className="bg-slate-900/70 border border-slate-800 rounded-2xl p-6 shadow-xl flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Send className="w-4 h-4 text-teal-400" />
              <h3 className="text-sm font-bold text-white tracking-tight">Test Delivery Handshake</h3>
            </div>
            <p className="text-xs text-slate-400 mb-4 leading-relaxed">
              Dispatch a test message through this SMTP server to verify hostname, port, and authentication credentials.
            </p>

            <form onSubmit={handleTest} className="space-y-3">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">
                  Recipient Test Address
                </label>
                <input
                  type="email"
                  required
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="your-email@example.com"
                  className="w-full px-3.5 py-2 rounded-xl bg-slate-950/80 border border-slate-700 text-xs text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
                />
              </div>

              <button
                type="submit"
                disabled={testing || !form.smtp_host}
                className="w-full py-2.5 px-4 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs flex items-center justify-center gap-2 shadow-lg shadow-indigo-600/25 transition-all disabled:opacity-50"
              >
                <Send className="w-3.5 h-3.5" />
                <span>{testing ? "Testing Handshake..." : "Send Test Email"}</span>
              </button>
            </form>

            {testResult && (
              <div
                className={`mt-4 p-3.5 rounded-xl border text-xs ${
                  testResult.ok
                    ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300"
                    : "bg-rose-500/10 border-rose-500/30 text-rose-300"
                }`}
              >
                <div className="font-bold flex items-center gap-1.5 mb-1">
                  {testResult.ok ? (
                    <CheckCircle2 className="w-4 h-4" />
                  ) : (
                    <AlertTriangle className="w-4 h-4" />
                  )}
                  <span>{testResult.ok ? "Delivery Verified" : "Handshake Failed"}</span>
                </div>
                <div className="text-[11px] leading-relaxed break-words font-mono">
                  {testResult.message}
                </div>
              </div>
            )}
          </div>

          <div className="mt-6 pt-4 border-t border-slate-800 text-[11px] text-slate-500">
            Tenant companies can override these settings under their own Company Settings page.
          </div>
        </div>
      </div>

      {/* Outgoing Email Logs Table */}
      <div className="bg-slate-900/70 border border-slate-800 rounded-2xl shadow-xl overflow-hidden">
        <div className="px-5 py-3.5 border-b border-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-slate-400" />
            <h3 className="text-sm font-bold text-white tracking-tight">Outgoing Email Logs</h3>
            <span className="text-xs text-slate-500">({logs.length} recent messages)</span>
          </div>
          <button
            onClick={loadLogs}
            disabled={loadingLogs}
            className="text-xs text-slate-400 hover:text-white flex items-center gap-1 transition-colors"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loadingLogs ? "animate-spin text-teal-400" : ""}`} />
            <span>Refresh Logs</span>
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse text-xs">
            <thead>
              <tr className="border-b border-slate-800 bg-slate-950/60 text-slate-400 font-bold uppercase tracking-wider text-[10px]">
                <th className="py-2.5 px-4">Dispatched At</th>
                <th className="py-2.5 px-4">Account / Origin</th>
                <th className="py-2.5 px-4">Recipient</th>
                <th className="py-2.5 px-4">Subject</th>
                <th className="py-2.5 px-4">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/60 text-slate-300">
              {logs.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-8 text-center text-slate-500 text-xs">
                    {loadingLogs ? "Loading email records..." : "No email logs found."}
                  </td>
                </tr>
              ) : (
                logs.map((log, idx) => {
                  const isSent = (log.status || "sent") === "sent";
                  return (
                    <tr key={log.id || idx} className="hover:bg-slate-800/40 transition-colors">
                      <td className="py-2.5 px-4 text-slate-400 font-mono text-[11px] whitespace-nowrap">
                        {log.sent_at || log.created_at || "—"}
                      </td>
                      <td className="py-2.5 px-4 text-white font-medium">
                        {log.account || "Default"}
                      </td>
                      <td className="py-2.5 px-4 text-slate-300 font-mono text-[11px]">
                        {log.recipient || log.to_email || "—"}
                      </td>
                      <td className="py-2.5 px-4 text-slate-200 max-w-xs truncate">
                        {log.subject || "Notification"}
                      </td>
                      <td className="py-2.5 px-4">
                        {isSent ? (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
                            Sent
                          </span>
                        ) : (
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span
                              title={log.error || "Failed delivery"}
                              className="px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase bg-rose-500/15 text-rose-400 border border-rose-500/30 cursor-help"
                            >
                              Failed
                            </span>
                            {log.error && (
                              <span className="text-[10px] text-rose-400/80 truncate max-w-[130px]" title={log.error}>
                                {log.error}
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
